import axios from 'axios';
import querystring from 'querystring';
import jwtDecode from 'jwt-decode';
import { get, omit } from 'lodash';
import history from 'utils/history';
import { formatErrorMessage } from './errorMessageHelper';
import { checkTokenExpiration, checkRefreshTokenExpiration, clearTokens } from 'utils/token';
import { refreshSession } from 'utils/session';

import { adminUrl, keycloakUrl, realm, clientId, redirectUri } from 'config/environment';

const formatAxiosError = (err) => {
  const responseData = get(err, 'response.data', {});

  return formatErrorMessage({
    ...responseData,
    status: get(responseData, 'status', get(err, 'response.status')),
  });
};

axios.interceptors.request.use(
  async (config) => {
    window.requestInProgress = true;

    if (
      config.url.includes(keycloakUrl) ||
      config.url.includes('update-password-with-check') ||
      config.url.includes('send-mail/forgot') ||
      config.url.includes('/api/wizard') ||
      config.url.includes('/api/email/correlationId') ||
      config.url.includes('login')
    )
      return Promise.resolve(config);

    // NXT-598: a protected request must never reach the transport without
    // usable credentials. Missing credentials or an unsuccessful refresh
    // clear the stale tokens, start the Keycloak login flow and cancel the
    // request so the backend is not hit with unauthenticated calls (401s).
    const abortUnauthenticatedRequest = (sessionHandled = false) => {
      if (!sessionHandled) {
        clearTokens();
        history.push('/login');
      }
      window.requestInProgress = false;
      return Promise.reject(new axios.Cancel('Request aborted: no valid session'));
    };

    const token = checkTokenExpiration();
    if (token && token !== 'expired') {
      config.headers['Authorization'] = 'Bearer ' + token;
      config.withCredentials = false;
    } else if (token && token === 'expired') {
      const refreshToken1 = checkRefreshTokenExpiration();
      if (refreshToken1 && refreshToken1 !== 'expired') {
        const { error, response, sessionHandled } = await refreshSession(refreshToken, refreshToken1);
        // A refresh rejected by the auth saga's role checks (or superseded
        // by logout) must not authorize a waiting request either.
        if (!error && checkTokenExpiration() === response.access_token) {
          config.headers['Authorization'] = 'Bearer ' + response.access_token;
          config.withCredentials = false;
        } else {
          return abortUnauthenticatedRequest(sessionHandled);
        }
      } else {
        return abortUnauthenticatedRequest();
      }
    } else if (!token) {
      return abortUnauthenticatedRequest();
    }
    return Promise.resolve(config);
  },
  (error) => {
    Promise.reject(error);
  }
);

axios.interceptors.response.use(function (response) {
  window.requestInProgress = false;
  return response;
});

const login = ({ username, password }) => {
  const payload = querystring.stringify({
    username: username,
    password: password,
  });
  const config = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
  };

  return axios
    .post(`${adminUrl}/api/login`, payload, config)
    .then((response) => {
      return {
        response: {
          ...get(response, 'data', {}),
          decoded: jwtDecode(get(response, 'data.access_token')),
        },
      };
    })
    .catch((err) => {
      return {
        error: formatErrorMessage(get(err, 'response.data', {})),
      };
    });
};

const getRoleManagementRepresantation = () => {
  return axios
    .get(`${adminUrl}/api/role-management`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const requestPasswordReset = (email) => {
  return axios
    .get(`${adminUrl}/api/wizard?email=${email}`)
    .then((response) => {
      const correlationId = get(response, 'data.correlationId');
      return axios
        .patch(`${adminUrl}/api/email/correlationId/${correlationId}/forgot-password `)
        .then((response) => response)
        .catch((err) => ({
          error: formatErrorMessage(get(err, 'response.data', {})),
        }));
    })
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const resetPassword = (data) => {
  return axios
    .put(`${adminUrl}/api/credentials/update-password-with-check`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const resetUserPassword = (data) => {
  const encodedUserName = encodeURIComponent(data.email);
  return axios
    .put(`${adminUrl}/api/login/reset-password?email=${encodedUserName}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const refreshToken = (token) => {
  const payload = querystring.stringify({
    client_id: clientId,
    refresh_token: token,
    grant_type: 'refresh_token',
  });
  const config = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axios
    .post(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`, payload, config)
    .then((response) => {
      return {
        response: {
          ...get(response, 'data', {}),
          decoded: jwtDecode(get(response, 'data.access_token')),
          isRefresh: true,
        },
      };
    })
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getUsers = (data = {}) => {
  let requestUrl = `${adminUrl}/api/users?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.role) {
    requestUrl += `&role=${get(data, 'role')}`;
  }
  if (data.usernameContains && data.usernameContains.length)
    requestUrl += `&usernameContains=${encodeURIComponent(get(data, 'usernameContains'))}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;
  if (data.canBeAttachedToLicence) requestUrl += `&canBeAttachedToLicence=${get(data, 'canBeAttachedToLicence', '')}`;
  if (typeof data.isActive !== 'undefined') requestUrl += `&isActive=${data.isActive}`;

  return axios
    .get(requestUrl)
    .then((response) => {
      return {
        response: typeof response.data === 'string' ? [] : response.data,
      };
    })
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getUser = (id) => {
  return axios
    .get(`${adminUrl}/api/users/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createStudioUser = (data) => {
  return axios
    .post(`${adminUrl}/api/users`, omit(data, 'options'))
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createStudioUserByAdmin = (data) => {
  return axios
    .post(`${adminUrl}/api/ems/user/studio`, omit(data, 'options'))
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createHomeUser = (data) => {
  return axios
    .post(`${adminUrl}/api/ems/user/home`, omit(data, 'options'))
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createRentalUser = (data) => {
  return axios
    .post(`${adminUrl}/api/users/rental`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateUser = (id, data) => {
  return axios
    .put(`${adminUrl}/api/users?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatAxiosError(err),
    }));
};

const updateUserCredits = (id, data) => {
  return axios
    .put(`${adminUrl}/api/users?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateUserByAdmin = (id, data) => {
  return axios
    .put(`${adminUrl}/api/ems/user?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatAxiosError(err),
    }));
};

const deleteUser = (id) => {
  return axios
    .delete(`${adminUrl}/api/users?ids=${id}`)
    .then((response) => ({ response: response.data || 'success' }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deleteUsers = async (data) => {
  const res = await data.reduce(async (acc, id) => {
    const { response, error } = await deleteUser(id);
    if (error)
      return {
        error: formatErrorMessage(get(error, 'response.data', {})),
      };
    if (response) return { response: response.data || 'success' };
  }, {});
  return res;
};

const requestResetPasswordLink = (data) => {
  return axios
    .post(`${adminUrl}/api/credentials`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateUserPassword = (data) => {
  return axios
    .put(`${adminUrl}/api/users/password`, {
      password: data.password,
      userForUpdateId: data.userId,
      temporary: true,
    })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateAdminPassword = (data) => {
  return axios
    .put(`${adminUrl}/api/members/password`, {
      password: data.password,
      memberForUpdateId: data.memberId,
      temporary: true,
    })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getPowerboxList = (data = {}) => {
  let requestUrl = `${adminUrl}/api/powerboxes?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.powerboxContains && data.powerboxContains.length)
    requestUrl += `&powerboxContains=${encodeURIComponent(get(data, 'powerboxContains'))}`;

  if (data.powerboxDisplayScope)
    requestUrl += `&canBeAttachedToLicense=${get(data, 'powerboxDisplayScope') === 'assigned' ? false : true}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;
  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getPowerboxListByLicense = (data = {}) => {
  console.log(data, data.canBeAttachedToLicense, data.canBeAttachedToAddOnLicense);
  let requestUrl = `${adminUrl}/api/powerboxes?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.powerboxContains && data.powerboxContains.length)
    requestUrl += `&powerboxContains=${encodeURIComponent(get(data, 'powerboxContains'))}`;

  if (data.powerboxDisplayScope)
    requestUrl += `&canBeAttachedToLicense=${get(data, 'powerboxDisplayScope') === 'assigned' ? false : true}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;
  requestUrl += `&canBeAttachedToLicense=${get(data, 'canBeAttachedToLicense', 'true')}&canBeAttachedToAddOnLicense=${get(data, 'canBeAttachedToAddOnLicense', 'false')}`;
  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getPowerbox = (id) => {
  return axios
    .get(`${adminUrl}/api/powerboxes/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createPowerbox = (data) => {
  return axios
    .post(`${adminUrl}/api/powerboxes`, omit(data, 'callback'))
    .then((response) => {
      if (data.callback) data.callback();
      return { response: response.data };
    })
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const assignPowerbox = (data) => {
  return axios
    .put(`${adminUrl}/api/powerboxes`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updatePowerbox = (id, data) => {
  return axios
    .put(`${adminUrl}/api/powerboxes/${id}`, omit(data, ['options', 'callback']))
    .then((response) => {
      if (data.callback) data.callback();
      return { response: { ...response.data, options: data.options } };
    })
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deletePowerbox = (id) => {
  return axios
    .delete(`${adminUrl}/api/powerboxes/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getOrganizationList = (data = {}) => {
  let requestUrl = `${adminUrl}/api/organization?size=${data.size || 100}&page=${data.page || 0}`;

  if (data.nameContains) requestUrl += `&nameContains=${encodeURIComponent(get(data, 'nameContains', ''))}`;
  if (data.parentOrganization) requestUrl += `&parentOrganizationId=${get(data, 'parentOrganization')}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;
  if (data.type) {
    requestUrl += `&type=${get(data, 'type', '')}`;
  }
  if (data.types?.length) {
    data.types.forEach((type) => {
      requestUrl += `&type=${type}`;
    });
  }
  if (typeof data.isActive !== 'undefined') {
    requestUrl += `&isActive=${data.isActive}`;
  }

  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

//api call returns all child organizations of the actual organization
const getChildOrganizationList = (data = {}) => {
  let requestUrl = `${adminUrl}/api/organization/childs?size=${data.size || 100}&page=${data.page || 0}`;

  requestUrl += `&nameContains=${encodeURIComponent(get(data, 'nameContains', ''))}`;
  if (data.type) {
    requestUrl += `&type=${get(data, 'type', '')}`;
  }

  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getOrganization = (id) => {
  return axios
    .get(`${adminUrl}/api/organization/own`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getChildOrganization = (id) => {
  return axios
    .get(`${adminUrl}/api/organization/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getOrganizationTypes = (id) => {
  return axios
    .get(`${adminUrl}/api/organization/types`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createOrganization = (data) => {
  return axios
    .post(`${adminUrl}/api/organization`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateOrganization = (id, data) => {
  return axios
    .put(`${adminUrl}/api/organization?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateOrganizationByAdmin = (id, data) => {
  return axios
    .put(`${adminUrl}/api/ems/organization?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deleteOrganization = (id) => {
  return axios
    .delete(`${adminUrl}/api/ems/organization?ids=${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getBlacklistEntries = (data) => {
  let requestUrl = `${adminUrl}/api/deactivation?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.blacklistContains && data.blacklistContains.length)
    requestUrl += `&deactivationContains=${encodeURIComponent(get(data, 'blacklistContains'))}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;

  requestUrl += `&entityType=${get(data, 'entityType')}`;
  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getBlacklistEntry = (id) => {
  return axios
    .get(`${adminUrl}/api/deactivation/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const moveToBlacklist = (data) => {
  return axios
    .post(`${adminUrl}/api/deactivation`, omit(data, ['organizationId', 'userId']))
    .then((response) => ({
      response: {
        data: response.data,
        organizationId: data?.organizationId,
        userId: data?.userId,
      },
    }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deleteFromBlacklist = (id) => {
  return axios
    .delete(`${adminUrl}/api/deactivation/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createMember = (data) => {
  return axios
    .post(`${adminUrl}/api/members`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createMemberByAdmin = (data) => {
  return axios
    .post(`${adminUrl}/api/ems/member`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateMember = (id, data) => {
  return axios
    .put(`${adminUrl}/api/members?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateMemberByAdmin = (id, data) => {
  return axios
    .put(`${adminUrl}/api/ems/member?id=${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deleteMember = (id) => {
  return axios
    .delete(`${adminUrl}/api/members?ids=${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deleteMemberByAdmin = (id) => {
  return axios
    .delete(`${adminUrl}/api/ems/member?ids=${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateMemberPassword = (data) => {
  return axios
    .put(`${adminUrl}/api/members/password`, {
      password: data.password,
      memberForUpdateId: data.id,
    })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getMembers = (data = {}) => {
  let requestUrl = `${adminUrl}/api/members?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.memberContains && data.memberContains.length)
    requestUrl += `&memberContains=${encodeURIComponent(get(data, 'memberContains'))}`;
  if (data.role && data.role.length) requestUrl += `&role=${get(data, 'role')}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;
  if (typeof data.isActive !== 'undefined') requestUrl += `&isActive=${data.isActive}`;

  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getMember = (id) => {
  return axios
    .get(`${adminUrl}/api/members/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getRoles = () => {
  return axios
    .get(`${adminUrl}/api/members/roles`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getLicenseTemplates = () => {
  return axios
    .get(`${adminUrl}/api/license-templates`)
    .then((response) => ({ response: response.data.filter((el) => Number(el.id) !== 6) }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getLicenseTemplate = (id) => {
  return axios
    .get(`${adminUrl}/api/license-templates/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createLicenseTemplate = (data) => {
  return axios
    .post(`${adminUrl}/api/license-templates`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateLicenseTemplate = (data) => {
  return axios
    .put(`${adminUrl}/api/license-templates`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deleteLicenseTemplate = (id) => {
  return axios
    .delete(`${adminUrl}/api/license-templates/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const createLicense = (data) => {
  return axios
    .post(`${adminUrl}/api/licenses`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const updateLicense = (id, data) => {
  return axios
    .put(`${adminUrl}/api/licenses/${id}`, omit(data, 'options'))
    .then((response) => ({ response: { ...response.data, options: data.options } }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const assignLicense = (data) => {
  return axios
    .put(`${adminUrl}/api/licenses`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const assignPoolLicense = (data) => {
  return axios
    .post(`${adminUrl}/api/licenses/share`, data)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getLicenses = (data = {}) => {
  let requestUrl = `${adminUrl}/api/licenses?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.licenseContains && data.licenseContains.length)
    requestUrl += `&licenseContains=${encodeURIComponent(get(data, 'licenseContains'))}`;
  if (data.canBeAssigned) requestUrl += `&canBeAssigned=${data.canBeAssigned}`;
  if (data.sharedLicense) requestUrl += `&sharedLicenses=true`;
  if (data.assigneeTypeStrings) requestUrl += `&assigneeTypeStrings=${data.assigneeTypeStrings}`;
  if (data.organizationId) requestUrl += `&organizationId=${data.organizationId}`;
  if (data.sortBy)
    requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}&sort.${data.sortBy}.order=0`;

  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => {
      return {
        error: formatErrorMessage(get(err, 'response.data', {})),
      };
    });
};

const getLicense = (id) => {
  return axios
    .get(`${adminUrl}/api/licenses/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getQrCode = (id) => {
  return axios
    .get(`${adminUrl}/api/users/qr/${id}`, {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf',
      },
    })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

export const getApplications = () => {
  return axios
    .get(`${adminUrl}/api/applications`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({ error: formatErrorMessage(get(err, 'response.data', {})) }));
};

export const getTrainingSources = () => {
  return axios
    .get(`${adminUrl}/api/applications/training-sources`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({ error: formatErrorMessage(get(err, 'response.data', {})) }));
};

export const getUserTrainings = ({
  id,
  page = 0,
  size = 100,
  anyTextFieldContains = '',
  sortBy = 'id',
  sortDirection = 'ASC',
  application,
  source,
}) => {
  const params = new URLSearchParams({ page, size, anyTextFieldContains });
  if (sortBy) {
    params.set(`sort.${sortBy}.direction`, sortDirection);
    params.set(`sort.${sortBy}.order`, '0');
  }
  if (application) params.set('application', application);
  if (source) params.set('source', source);

  return axios
    .get(`${adminUrl}/api/trainings/user/${id}?${params.toString()}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

export const getUserAchievements = (id) => {
  return axios
    .get(`${adminUrl}/api/users-achievements/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getAccessTokenFromCode = (code) => {
  const keycloakEndpointUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;
  const payload = querystring.stringify({
    client_id: clientId,
    code: code,
    grant_type: 'authorization_code',
    redirect_uri: redirectUri,
  });
  const config = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };
  return axios
    .post(keycloakEndpointUrl, payload, config)
    .then(async (res) => {
      return {
        response: {
          ...get(res, 'data', {}),
          decoded: jwtDecode(get(res, 'data.access_token')),
        },
      };
    })
    .catch((err) => {
      return {
        error: formatErrorMessage(get(err, 'response.data', {})),
      };
    });
};

const getIncidentReports = (data = {}) => {
  let requestUrl = `${adminUrl}/api/incident-report/filter?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.sortBy) requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}`;
  if (data.reportType) requestUrl += `&reportType=${data.reportType}`;
  if (data.application) requestUrl += `&application=${data.application}`;
  if (data.autoGenerated !== undefined && data.autoGenerated !== '')
    requestUrl += `&autoGenerated=${data.autoGenerated}`;
  if (data.incidentContains && data.incidentContains.length)
    requestUrl += `&incidentContains=${encodeURIComponent(data.incidentContains)}`;
  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getFileDownloadLink = (data) => {
  return axios({
    url: `${adminUrl}/api/incident-report/download-link?fileDir=${data.dir}&fileKey=${data.key}`,
    method: 'get',
  })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: {
        ...get(err, 'response.data', {}),
        message: 'Failed to get file download link',
      },
    }));
};

const deleteIncidentReports = () => {
  return {
    error: {
      message: 'Not implemented',
    },
  };
};

// @return bool isBeautyCareAdmin for current loggedIn user
const isBeautyCareAdmin = () => {
  return axios
    .get(`${adminUrl}/api/members/am-i-beautycare-admin`)
    .then((response) => ({ response: response.data.isBeautyCareAdmin }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getAppLogs = (data) => {
  let requestUrl = `${adminUrl}/api/app-logs/filter?size=${data.size || 100}&page=${data.page || 0}`;
  if (data.sortBy) requestUrl += `&sort.${data.sortBy}.direction=${get(data, 'sortDirection', '')}`;
  if (data.application) requestUrl += `&application=${data.application}`;
  if (data.contains && data.contains.length) requestUrl += `&contains=${encodeURIComponent(data.contains)}`;
  return axios
    .get(requestUrl)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const getAppLogDownloadLink = (data) => {
  return axios({
    url: `${adminUrl}/api/app-logs/download-link?fileKey=${data.key}`,
    method: 'get',
  })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: {
        ...get(err, 'response.data', {}),
        message: 'Failed to get file download link',
      },
    }));
};

const getOrganizationsHierarchy = () => {
  return axios
    .get(`${adminUrl}/api/organization/hierarchy`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: {
        ...get(err, 'response.data', {}),
        message: 'Failed to get organizations',
      },
    }));
};

const deactivateUser = (id) => {
  return axios
    .post(`${adminUrl}/api/users/deactivate/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const restoreUser = (id) => {
  return axios
    .post(`${adminUrl}/api/users/restore/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deactivateMember = (id) => {
  return axios
    .post(`${adminUrl}/api/members/deactivate/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const restoreMember = (id) => {
  return axios
    .post(`${adminUrl}/api/members/restore/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const deactivateOrganization = (id) => {
  return axios
    .post(`${adminUrl}/api/organization/deactivate/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const restoreOrganization = (id) => {
  return axios
    .post(`${adminUrl}/api/organization/restore/${id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const assignParentOrganization = (data) => {
  return axios
    .put(`${adminUrl}/api/organization/parent?id=${data.id}&parentId=${data.parentId}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const assignRentalLicense = (payload) => {
  return axios
    .post(`${adminUrl}/api/licenses/rental`, payload)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const revokeRentalLicense = (payload) => {
  return axios
    .delete(`${adminUrl}/api/licenses/rental`, { data: payload })
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const convertStudioUserToRental = (payload) => {
  return axios
    .post(`${adminUrl}/api/users/convert/rental/${payload.id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const convertRentalUserToStudio = (payload) => {
  return axios
    .post(`${adminUrl}/api/users/convert/studio/${payload.id}`)
    .then((response) => ({ response: response.data }))
    .catch((err) => ({
      error: formatErrorMessage(get(err, 'response.data', {})),
    }));
};

const api = {
  login,
  refreshToken,
  getUsers,
  getUser,
  updateUser,
  updateUserCredits,
  updateUserByAdmin,
  deleteUser,
  deleteUsers,
  createHomeUser,
  createStudioUser,
  requestPasswordReset,
  resetPassword,
  updateUserPassword,
  updateAdminPassword,
  getPowerboxList,
  getPowerboxListByLicense,
  getPowerbox,
  createPowerbox,
  assignPowerbox,
  deletePowerbox,
  updatePowerbox,
  createOrganization,
  updateOrganization,
  updateOrganizationByAdmin,
  deleteOrganization,
  getOrganization,
  getOrganizationList,
  getChildOrganizationList,
  getRoleManagementRepresantation,
  getBlacklistEntries,
  getBlacklistEntry,
  moveToBlacklist,
  getOrganizationTypes,
  getChildOrganization,
  deleteFromBlacklist,
  getMember,
  getMembers,
  updateMember,
  updateMemberByAdmin,
  createMember,
  createMemberByAdmin,
  deleteMember,
  updateMemberPassword,
  deleteMemberByAdmin,
  getRoles,
  getLicenseTemplates,
  getLicenseTemplate,
  createLicenseTemplate,
  updateLicenseTemplate,
  deleteLicenseTemplate,
  getLicenses,
  createLicense,
  updateLicense,
  assignLicense,
  assignPoolLicense,
  requestResetPasswordLink,
  createStudioUserByAdmin,
  getLicense,
  getQrCode,
  getUserTrainings,
  getApplications,
  getTrainingSources,
  getUserAchievements,
  resetUserPassword,
  getAccessTokenFromCode,
  getIncidentReports,
  getFileDownloadLink,
  deleteIncidentReports,
  isBeautyCareAdmin,
  getAppLogs,
  getAppLogDownloadLink,
  getOrganizationsHierarchy,
  deactivateUser,
  restoreUser,
  assignParentOrganization,
  deactivateMember,
  restoreMember,
  deactivateOrganization,
  restoreOrganization,
  assignRentalLicense,
  revokeRentalLicense,
  createRentalUser,
  convertRentalUserToStudio,
  convertStudioUserToRental,
};
export default api;
